import type { ReactNode } from "react";
import { Button, Typography } from "@foodpilot/foods";
import { Stack, useTheme } from "@mui/material";

import { FoodsBottomSheetDialog } from "src/components/Dialog/FoodsBottomSheetDialog";

export type EntityQuitConfirmDialogProps = {
    open: boolean;
    title: ReactNode;
    content?: ReactNode;
    cancelLabel: ReactNode;
    confirmLabel: ReactNode;
    confirmDisabled?: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    onDismiss?: () => void;
};

/** Shared destructive-exit confirmation shell for entity forms. */
export const EntityQuitConfirmDialog = (props: EntityQuitConfirmDialogProps) => {
    const {
        open,
        title,
        content,
        cancelLabel,
        confirmLabel,
        confirmDisabled = false,
        onCancel,
        onConfirm,
        onDismiss = onCancel,
    } = props;
    const theme = useTheme();
    const hasContent = content !== undefined && content !== null;

    return (
        <FoodsBottomSheetDialog
            open={open}
            setOpen={() => undefined}
            onClose={onDismiss}
            aria-labelledby="entity-quit-confirm-title"
            aria-describedby={hasContent ? "entity-quit-confirm-content" : undefined}
            title={
                <Typography id="entity-quit-confirm-title" component="h2" variant="h3">
                    {title}
                </Typography>
            }
            content={
                <Stack gap={theme.spacing(3)} sx={{ padding: theme.spacing(3) }}>
                    {hasContent && (
                        <Typography
                            id="entity-quit-confirm-content"
                            component="div"
                            variant="body-regular"
                            sx={{ color: theme.custom.grey[2000] }}
                        >
                            {content}
                        </Typography>
                    )}
                    <Stack direction="row" justifyContent="flex-end" gap={theme.spacing(1)}>
                        <Button variant="text" onClick={onCancel}>
                            {cancelLabel}
                        </Button>
                        <Button variant="primary" disabled={confirmDisabled} onClick={onConfirm}>
                            {confirmLabel}
                        </Button>
                    </Stack>
                </Stack>
            }
        />
    );
};
